<template>
  <avs-card class="voucher-detail-container">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <v-row class="row mb-4">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" md="3">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" md="3" class="d-flex align-center">
              <v-switch
                style="margin-top: 5px;"
                v-model="showExpired"
                :label="$t('Bons expirés')"
                @change="applyFilters"
                inset
              />
            </v-col>

            <v-col cols="12" md="3" style="margin-top: -13px;">
              <v-autocomplete
                v-model="selectedDepartments"
                clearable
                :items="departments"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Departements')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-sitemap"
              />
            </v-col>

            <v-col cols="12" md="3" style="margin-top: -13px;">
              <v-autocomplete
                v-model="selectedShop"
                clearable
                :items="shops"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Stations')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col cols="12" md="3" style="margin-top: -13px;">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

            <!-- Actions (Export / slot parent) -->
            <v-col cols="12" md="3" class="d-flex align-center justify-end" style="margin-top: -13px;">
              <slot name="actions" v-bind:dataset="items" />
            </v-col>
          </v-row>

          <!-- ===== STATS ===== -->
          <v-row class="stats-section">
            <v-col cols="12">
              <div class="stats-wrapper">
                <div class="stats-container">
                  <!-- Carte Total Général -->
                  <v-card class="stat-card stat-card-main" elevation="4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ $t('Montant total') }}</div>
                        <div class="stat-value-mobile">
                          {{ formatNumber(totalAmount) }}
                          <span class="stat-unit-mobile">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
                    <div class="stat-secondary mt-2">
                      <v-icon small color="white">mdi-barrel</v-icon>
                      {{ formatNumber(totalLiters) }} L
                      <v-icon small class="ml-4" color="white">mdi-ticket-confirmation</v-icon>
                      {{ formatNumber(totalCount) }} {{ $t('bons') }}
                    </div>
                  </v-card>

                  <!-- Par produit -->
                  <v-card
                    v-for="(p, idx) in totalByProduct"
                    :key="idx"
                    class="stat-card stat-card-product bgTable"
                    elevation="3"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ p.name || '—' }}</div>
                        <div class="stat-value-mobile">
                          {{ formatNumber(p.total_amount) }}
                          <span class="stat-unit-mobile">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40">mdi-gas-station</v-icon>
                      </div>
                    </div>
                    <div class="stat-secondary-product">
                      <v-icon x-small>mdi-gauge</v-icon>
                      {{ formatNumber(p.total_liters) }} L
                    </div>
                  </v-card>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 mobile-header">
        <div class="mobile-page-header px-4 pt-4 pb-3">
          <div class="d-flex align-center">
            <v-icon large color="primary" class="mr-2">mdi-ticket-percent</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">
              {{ $t("Bons d'achat") }}
            </h3>
          </div>
        </div>

        <!-- Filtres dans un expander -->
        <v-expansion-panels flat class="px-4 mb-3">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-filter-variant</v-icon>
                {{ $t('Filtres') }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="mb-3"
              />

              <v-menu v-model="menuStartDate" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    class="mb-3"
                  />
                </template>
                <v-date-picker v-model="startDate" @input="applyFilters" />
              </v-menu>

              <v-menu v-model="menuEndDate" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    class="mb-3"
                  />
                </template>
                <v-date-picker v-model="endDate" @input="applyFilters" />
              </v-menu>

              <v-switch
                class="mb-3"
                v-model="showExpired"
                :label="$t('Bons expirés')"
                @change="applyFilters"
                inset
              />

              <v-autocomplete
                v-model="selectedDepartments"
                clearable
                :items="departments"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Departements')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-sitemap"
                class="mb-3"
              />

              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
                class="mb-3"
              />

              <v-autocomplete
                v-model="selectedShop"
                clearable
                :items="shops"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Stations')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
                class="mb-3"
              />

              <v-autocomplete
                v-model="selectedVehicle"
                clearable
                :items="vehicles"
                @input="applyFilters"
                :item-text="formatVehicle"
                item-value="id"
                :label="$t('Vehicules')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-car"
                class="mb-3"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Bouton nouveau bon -->
        <div class="d-flex align-center justify-end px-4 mb-3">
          <slot name="actions-mobile" v-bind:dataset="items" />
        </div>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop table -->
      <div v-if="$vuetify.breakpoint.mdAndUp" class="col-12 mt-0">
        <v-data-table
          class="ma-2 bgTable"
          :headers="headers"
          :loading-text="$t('Chargement des données')"
          :no-results-text="$t('Aucune donnée disponible')"
          :loading="isLoading"
          :items="items"
          disable-pagination
          hide-default-footer
          item-key="id"
          dense
          :item-class="rowClass"
          @dblclick:row="onRowDblClick"
        >
          <template v-slot:item.purchase_amount="{ item }">
            {{ formatNumber(item.purchase_amount) }}
          </template>
          <template v-slot:item.purchase_liters="{ item }">
            {{ formatNumber(item.purchase_liters) }}
          </template>
          <template v-slot:item.liter_remaining="{ item }">
            {{ formatNumber(item.liter_remaining) }}
          </template>
          <template v-slot:item.remaining_amount="{ item }">
            {{ formatNumber(item.remaining_amount) }}
          </template>

          <template v-slot:item.company_product_name="{ item }">
            <v-chip small label color="accent" text-color="white">
              <v-icon left x-small>mdi-gas-station</v-icon>
              {{ item.company_product?.name || '—' }}
            </v-chip>
          </template>

          <!-- Conso (%) -->
          <template v-slot:item.remaining_percent="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :value="remainingPercent(item)"
                :color="colorForRemaining(remainingPercent(item))"
                height="8"
                rounded
                style="min-width: 120px"
              />
              <span class="ml-2">{{ remainingPercent(item) }}%</span>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <component :is="handleStatusComponent(item)" />
          </template>

          <!-- Conducteur + matricule -->
          <template v-slot:item.driver="{ item }">
            <div>{{ getDriverName(item) }}</div>
            <div class="caption grey--text">
              {{ item.vehicle_detail?.registration_number || '—' }}
            </div>
          </template>

          <template v-slot:item.valid_until="{ item }">
            {{ toDatetime(item.valid_until) }}
          </template>

          <template v-slot:item.company_shop_name="{ item }">
            {{ locationName(item.company_shop) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <avs-button
                v-if="canAddExceptionalIncrease(item)"
                icon
                color="secondary"
                :iconI="'mdi-water-plus-outline'"
                label
                fab
                small
                :spantext="$t('Ajouter un litrage exceptionnel')"
                @click.stop="openExceptionalIncreaseDialog(item)"
              />
              <avs-editButton
                @click.stop="toEdit(item)"
                :disabled="item.vehicle_detail?.is_blocked"
                :spantext="$t('Modifier le credit carburant')"
              />
              <avs-button
                v-if="canApproveVouchers && item.status === 'pending'"
                :disabled="item.vehicle_detail?.is_blocked"
                icon
                color="success"
                :iconI="'mdi-check-decagram'"
                label
                fab
                small
                :spantext="$t('Traiter le bon (valider / rejeter)')"
                @click.stop="openDecisionDialog(item)"
              />
              <avs-button
                v-if="item.as_historied"
                icon
                color="secondary"
                :iconI="'mdi-history'"
                label
                fab
                small
                :spantext="$t('Historique des bons')"
                @click.stop="toView(item)"
              />
              <avs-button
                icon
                color="primary"
                :iconI="'mdi-finance'"
                label
                fab
                small
                :spantext="$t('Voir les transactions')"
                @click.stop="toTransactions(item)"
              />
              <avs-deleteButton
                v-if="canDelete(item)"
                :disabled="false"
                @click="handleOpenDeleteDialog(item)"
                :spantext="$t('actions.delete', { entity: $tc('categories.cfsi.name', 1).toLowerCase() })"
              />
              <avs-button
                v-else
                icon
                color="error"
                :iconI="'mdi-cancel'"
                label
                fab
                small
                :spantext="$t('Désactiver le bon')"
                @click.stop="handleOpenDeactivateDialog(item)"
              />
            </div>
          </template>
        </v-data-table>

        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Mobile cards -->
      <div v-else class="col-12 px-4">
        <v-skeleton-loader
          v-if="isLoading && !items.length"
          type="image, article, actions"
          class="mb-3"
        />
        <v-alert v-else-if="!items.length" type="info" dense outlined class="mb-3">
          {{ $t('messages.information.no_data') }}
        </v-alert>

        <v-card
          v-for="it in items"
          :key="it.id"
          class="mb-3 voucher-card"
          outlined
          @click="toEdit(it)"
        >
          <v-card-title class="py-3 d-flex align-center">
            <div class="voucher-title">
              <div class="veh text-truncate">
                {{ it.vehicle_detail?.registration_number || '—' }}
              </div>
              <div class="driver grey--text text--darken-1">
                {{ getDriverName(it) }}
              </div>
            </div>
            <v-spacer />
            <v-chip small label :color="statusColor(it)" text-color="white" class="text-capitalize">
              {{ statusLabel(it) }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t('Montant (XOF)') }}</div>
                <div class="value">{{ formatNumber(it.purchase_amount) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $tc('Litre') }}</div>
                <div class="value">{{ formatNumber(it.purchase_liters) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Restant (XOF)') }}</div>
                <div class="value">{{ formatNumber(it.remaining_amount) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Restant (L)') }}</div>
                <div class="value">{{ formatNumber(it.liter_remaining) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Date de fin') }}</div>
                <div class="value">{{ toDatetime(it.valid_until) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Site') }}</div>
                <div class="value">{{ locationName(it.company_shop) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Produit') }}</div>
                <div class="value">{{ it.company_product?.name || 'N/A' }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="py-2 d-flex align-center">
            <avs-button
              v-if="canAddExceptionalIncrease(it)"
              icon
              color="secondary"
              :iconI="'mdi-water-plus-outline'"
              label
              fab
              small
              :spantext="$t('Ajouter un litrage exceptionnel')"
              @click.stop="openExceptionalIncreaseDialog(it)"
            />
            <v-btn
              v-if="canApproveVouchers && it.status === 'pending'"
              icon
              color="success"
              :title="$t('Traiter')"
              class="mx-1"
              @click.stop="openDecisionDialog(it)"
            >
              <v-icon small>mdi-check-decagram</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              icon
              color="primary"
              :title="$t('actions.edit')"
              class="mx-1"
              @click.stop="toEdit(it)"
              :disabled="it.vehicle_detail?.is_blocked"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              color="info"
              :title="$t('Voir les transactions')"
              class="mx-1"
              @click.stop="toTransactions(it)"
            >
              <v-icon small>mdi-finance</v-icon>
            </v-btn>
            <v-btn
              icon
              color="secondary"
              :title="$t('Historique des bons')"
              class="mx-1"
              @click.stop="toView(it)"
            >
              <v-icon small>mdi-history</v-icon>
            </v-btn>
            <v-btn
              v-if="canDelete(it)"
              icon
              color="error"
              :title="$t('actions.delete')"
              class="mx-1"
              @click.stop="handleOpenDeleteDialog(it)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
            <v-btn
              v-else
              icon
              color="error"
              :title="$t('Désactiver le bon')"
              class="mx-1"
              @click.stop="handleOpenDeactivateDialog(it)"
            >
              <v-icon small>mdi-cancel</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <div ref="infiniteScrollTrigger" style="height: 1px;"></div>
        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Dialogs (inchangés) -->
      <v-dialog
        v-model="decisionDialogIsOpen"
        max-width="560"
        transition="dialog-bottom-transition"
        :fullscreen="$vuetify.breakpoint.xs"
      >
        <v-card class="rounded-xl" v-if="decisionTargetItem">
          <!-- Header -->
          <v-sheet color="primary" dark class="pa-4 d-flex align-center">
            <v-icon x-large class="mr-3">mdi-clipboard-check-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">{{ $t("Traiter le bon d'achat") }}</div>
              <div class="opacity-80 text-caption">{{ $t("Approuver ou rejeter ce bon. L'activation suivra la politique definie.") }}</div>
            </div>
          </v-sheet>

          <v-card-text class="pt-5">
            <v-row>
              <!-- Infos vehicule -->
              <v-col cols="12" md="6">
                <v-list dense two-line>
                  <v-subheader>{{ $t('Vehicule') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-car</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ decisionTargetItem.vehicle_detail?.registration_number || '-' }}</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Immatriculation') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-speedometer</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ formatNumber(decisionTargetItem.vehicle_detail?.kmh) || '0' }} km</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Kilometrage') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <!-- Infos bon -->
              <v-col cols="12" md="6">
                <v-list dense two-line>
                  <v-subheader>{{ $t('Bon') }}</v-subheader>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-cash</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ formatNumber(decisionTargetItem.purchase_amount) }} XOF</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Montant') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-gas-station</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ formatNumber(decisionTargetItem.purchase_liters) }} L</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('Litres') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-space-between">
            <v-btn text @click="closeDecisionDialog">
              <v-icon left>mdi-close</v-icon>{{ $t('commons.cancel') }}
            </v-btn>
            <div class="d-flex">
              <v-btn
                color="error"
                class="mr-3"
                :loading="deciding === 'reject'"
                :disabled="!!deciding"
                @click="confirmDecision('reject')"
              >
                <v-icon left>mdi-close-circle</v-icon>{{ $t('Rejeter') }}
              </v-btn>
              <v-btn
                color="success"
                :loading="deciding === 'approve'"
                :disabled="!!deciding"
                @click="confirmDecision('approve')"
              >
                <v-icon left>mdi-check-decagram</v-icon>{{ $t('Valider') }}
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <ExceptionalIncreaseDialog
        v-if="exceptionalIncreaseItem"
        v-model="exceptionalIncreaseDialog"
        :voucher="exceptionalIncreaseItem"
        @success="handleExceptionalIncreaseSuccess"
      />

      <!-- Dialog désactivation -->
      <v-dialog v-model="deactivateDialogIsOpen" max-width="480" transition="dialog-bottom-transition">
        <v-card>
          <v-card-title class="headline">
            {{ $t("Désactiver le bon d'achat") }}
          </v-card-title>
          <v-card-text>
            <p class="mb-2">
              {{
                $t(
                  "Êtes-vous sûr de vouloir désactiver ce bon ? Il ne pourra plus être utilisé pour de nouvelles transactions, mais l'historique sera conservé."
                )
              }}
            </p>
            <div class="mt-2 grey--text text--darken-1" v-if="deactivateTargetItem">
              <div>
                <strong>{{ $t('Vehicule') }}:</strong>
                {{ deactivateTargetItem.vehicle_detail?.registration_number || '—' }}
              </div>
              <div>
                <strong>{{ $t('Montant (XOF)') }}:</strong>
                {{ formatNumber(deactivateTargetItem.purchase_amount) }}
              </div>
              <div>
                <strong>{{ $tc('Litre') }}:</strong>
                {{ formatNumber(deactivateTargetItem.purchase_liters) }}
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text @click="handleCancelDeactivate">
              {{ $t('commons.cancel') }}
            </v-btn>
            <v-btn
              color="error"
              :loading="isDeactivating"
              :disabled="isDeactivating"
              @click="handleDeactivate"
            >
              {{ $t('Désactiver') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Bon d'achat"
      />
    </template>
  </avs-card>
</template>

<script>
import { debounce, truncate } from "lodash";
import CompanyVouchersService from "@/services/CompanyVouchersService";
import {
  ShopDetailService,
  ProductDetailService,
  CompanyVehiclesService,
  DepartmentService,
} from "@/services";
import { toDate, toDatetime } from "@/utils/datetime";

export default {
  name: "VoucherTable",
  props: {
    inspectionId: { type: Number, default: null },
  },
  data() {
    return {
      // list + paging
      items: [],
      page: 1,
      isFetchingMore: false,
      isLastPage: false,
      isLoading: false,

      exceptionalIncreaseDialog: false,
      exceptionalIncreaseItem: null,

      deactivateDialogIsOpen: false,
      deactivateTargetItem: null,
      isDeactivating: false,

      // filters
      search: "",
      selectedShop: null,
      selectedProduct: null,
      selectedVehicle: null,
      selectedDepartments: null,
      showExpired: false,
      startDate: null,
      endDate: null,
      menuStartDate: false,
      menuEndDate: false,

      // refs
      shops: [],
      products: [],
      vehicles: [],
      departments: [],

      // stats
      totalAmount: 0,
      totalLiters: 0,
      totalCount: 0,
      totalByProduct: [],

      // dialogs
      deleteDialogIsOpen: false,
      decisionDialogIsOpen: false,
      decisionTargetItem: null,
      deciding: null,
      selectedItem: null,

      headers: [
        { text: this.$t("Conducteur"), value: "driver" },
        { text: this.$t("Montant"), align: "start", value: "purchase_amount", sortable: false },
        { text: this.$tc("Litre"), value: "purchase_liters", sortable: false, align: "center" },
        { text: this.$t("Reste"), value: "remaining_amount", sortable: false },
        { text: this.$t("(L)"), value: "liter_remaining", sortable: false },
        { text: this.$t("Conso (%)"), value: "remaining_percent", sortable: false },
        { text: this.$t("Date de fin"), value: "valid_until", sortable: false },
        { text: this.$t("Site"), value: "company_shop_name", sortable: false },
        { text: this.$t("Produit"), value: "company_product_name", sortable: false },
        { text: this.$t("commons.actions"), value: "actions", align: "right", sortable: false, width: 100 },
      ],

      getVoucherDebounced: null,
    };
  },
  components: {
    ExceptionalIncreaseDialog: () =>
      import("@/components/ri3/companyVouchers/ExceptionalIncreaseDialog.vue"),
  },
  computed: {
    canApproveVouchers() {
      return !!(this.company && this.company.requires_voucher_approval);
    },
    company() {
      return this.$store.state.app.company;
    },
    department() {
      return this.$store.state.app.project;
    },
  },
  methods: {
    truncate,
    toDatetime,
    toDate,

    async refresh() {
      // ✅ ordre garanti : d’abord page 1, ensuite page 2
      await this.fetchInitial();
      await this.loadMoreVouchers();
    },

    formatDateToString(date) {
      return date.toISOString().split("T")[0];
    },

    canDelete(item) {
      if (!item) return false;
      if (item.as_historied) return false;
      if (this.isDisabled(item)) return false;
      return true;
    },

    // ----- Désactivation -----
    handleOpenDeactivateDialog(item) {
      this.deactivateTargetItem = item;
      this.deactivateDialogIsOpen = true;
    },
    handleCancelDeactivate() {
      this.deactivateDialogIsOpen = false;
      this.deactivateTargetItem = null;
    },
    async handleDeactivate() {
      if (!this.deactivateTargetItem) return;
      this.isDeactivating = true;
      try {
        await CompanyVouchersService.deactivate(this, this.deactivateTargetItem.id);

        this.$store.commit("app/message", {
          msg: this.$t("Bon désactivé avec succès."),
          color: "success",
        });

        await this.refresh();
      } catch (err) {
        this.$store.commit("app/message", {
          msg: this.$t("Une erreur s'est produite lors de la désactivation du bon."),
          color: "error",
        });
      } finally {
        this.isDeactivating = false;
        this.handleCancelDeactivate();
      }
    },

    canAddExceptionalIncrease(item) {
      const freq = (item.frequency || "Single").toLowerCase();
      const isRecurrent = freq !== "single";
      const isApproved = item.status === "approved" || item.status === "valid";
      // const notUsed = !item.is_all_used;
      const notBlocked = !item.vehicle_detail?.is_blocked;
      return isRecurrent && isApproved && notBlocked;
    },

    openExceptionalIncreaseDialog(item) {
      this.exceptionalIncreaseItem = item;
      this.exceptionalIncreaseDialog = true;
    },

    async handleExceptionalIncreaseSuccess(updatedVoucher) {
      const idx = this.items.findIndex((v) => v.id === updatedVoucher.id);
      if (idx !== -1) this.$set(this.items, idx, updatedVoucher);

      this.exceptionalIncreaseItem = null;
      this.exceptionalIncreaseDialog = false;

      await this.refresh();
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      return n.toLocaleString("fr-FR");
    },

    getDriverName(item) {
      if (
        item.vehicle_detail &&
        (item.vehicle_detail.driver_first_name || item.vehicle_detail.driver_last_name)
      ) {
        const first = item.vehicle_detail.driver_first_name || "";
        const last = item.vehicle_detail.driver_last_name || "";
        return `${first} ${last}`.trim();
      }
      return "N/A";
    },

    locationName(v) {
      return v ? v.name : this.$t("Tout le réseau");
    },

    formatVehicle(vehicle) {
      if (!vehicle) return "N/A";
      const reg = vehicle.registration_number || "N/A";
      const first = vehicle.driver_first_name || "";
      const last = vehicle.driver_last_name || "";
      return `${reg} - ${last} ${first}`.trim();
    },

    statusLabel(it) {
      const s = (it.status || "").toLowerCase();
      if (s === "pending") return this.$t("En attente");
      if (s === "approved" || s === "valid") return this.$t("Validé");
      if (s === "rejected") return this.$t("Rejeté");
      if (s === "closed" || s === "expired") return this.$t("Expiré");
      return s || "—";
    },

    statusColor(it) {
      const s = (it.status || "").toLowerCase();
      if (s === "pending") return "warning";
      if (s === "approved" || s === "valid") return "success";
      if (s === "rejected") return "error";
      if (s === "closed" || s === "expired") return "grey";
      return "primary";
    },

    handleStatusComponent() {
      return "Accepted";
    },

    rowClass(item) {
      return item.vehicle_detail?.is_blocked ? "blocked-row" : "";
    },

    remainingPercent(item) {
      const amount = Number(item.purchase_amount || 0);
      const remAmount = Number(item.remaining_amount || 0);
      const liters = Number(item.purchase_liters || 0);
      const remLiters = Number(item.liter_remaining || 0);

      let pct = 0;
      if (amount > 0) pct = ((amount - remAmount) / amount) * 100;
      else if (liters > 0) pct = ((liters - remLiters) / liters) * 100;

      pct = Math.max(0, Math.min(100, pct));
      return Math.round(pct);
    },

    colorForRemaining(pct) {
      if (pct <= 50) return "success";
      if (pct <= 80) return "warning";
      return "error";
    },

    isDisabled(item) {
      return item.purchase_liters !== item.liter_remaining;
    },

    onRowDblClick(event, { item }) {
      if (item.vehicle_detail?.is_blocked) {
        this.$store.commit("app/message", {
          msg: this.$t("Ce véhicule est bloqué, impossible de modifier le bon d'achat."),
          color: "warning",
        });
        return;
      }
      this.toEdit(item);
    },

    getQueryParams(extra = {}) {
      let department =
        this.selectedDepartments ??
        (this.department && this.department.id) ??
        null;

      return {
        search: this.search || undefined,
        start_date: this.startDate || undefined,
        end_date: this.endDate || undefined,
        shop: this.selectedShop || undefined,
        product: this.selectedProduct || undefined,
        vehicle: this.selectedVehicle || undefined,
        department: department || undefined,
        show_expired: this.showExpired || undefined,
        page: this.page,
        ...extra,
      };
    },

    async fetchInitial() {
      this.isLoading = true;
      this.page = 1;
      this.items = [];
      this.isLastPage = false;

      try {
        const res = await CompanyVouchersService.get(this, this.getQueryParams());
        const payload = res?.results ? res : { results: res };
        const results = payload.results || [];

        this.items = results;
        this.page = 2;

        const stats = payload.stats || {};
        this.totalAmount = Number(stats.total_amount || 0);
        this.totalLiters = Number(stats.total_liters || 0);
        this.totalCount = Number(
          payload.total_count || stats.total_count || results.length || 0
        );
        this.totalByProduct = stats.total_by_product || [];

        // ✅ si next est null => fin
        this.isLastPage = results.length === 0 || payload.next === null;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.permission_denied"),
            color: "error",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async loadMoreVouchers() {
      if (this.isFetchingMore || this.isLastPage) return;

      this.isFetchingMore = true;
      try {
        const res = await CompanyVouchersService.get(
          this,
          this.getQueryParams({ page: this.page })
        );

        const payload = res?.results ? res : { results: res };
        const results = payload.results || [];

        if (!results.length) {
          this.isLastPage = true;
        } else {
          const seen = new Set(this.items.map((i) => i.id));
          const add = results.filter((i) => !seen.has(i.id));
          this.items.push(...add);

          this.page += 1;

          if (payload.stats) {
            this.totalAmount = Number(payload.stats.total_amount ?? this.totalAmount);
            this.totalLiters = Number(payload.stats.total_liters ?? this.totalLiters);
            this.totalCount = Number(payload.total_count ?? payload.stats.total_count ?? this.totalCount);
            this.totalByProduct = payload.stats.total_by_product ?? this.totalByProduct;
          }

          if (payload.next === null) this.isLastPage = true;
        }
      } catch (e) {
        console.error("Erreur chargement bons :", e);
      } finally {
        this.isFetchingMore = false;
      }
    },

    onSearchChange() {
      this.getVoucherDebounced();
    },

    async applyFilters() {
      await this.refresh();
    },

    toEdit(item) {
      this.$router.push({ name: "VoucherCreateUpdate", params: { id: item.id } });
    },

    toView(item) {
      this.$router.push({ name: "CompanyVouchersHistory", params: { id: item.id } });
    },

    toTransactions(item) {
      const search = item?.vehicle_detail?.registration_number;
      if (!search) {
        this.$store.commit("app/message", {
          msg: this.$t("Aucune plaque de véhicule associée à ce bon."),
          color: "warning",
        });
        return;
      }
      this.$router.push({ name: "TransactionDetail", query: { search } });
    },

    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true;
      this.selectedItem = item;
    },

    handleCancel() {
      this.deleteDialogIsOpen = false;
    },

    async handleDelete() {
      try {
        await CompanyVouchersService.remove(this, this.selectedItem.id);
        this.items = this.items.filter((e) => e.id !== this.selectedItem.id);

        this.$store.commit("app/message", {
          msg: this.$t("messages.success.deleted", { entity: this.$tc("Bon d'achat", 2) }),
          color: "success",
        });

        await this.refresh();
      } finally {
        this.deleteDialogIsOpen = false;
      }
    },

    openDecisionDialog(item) {
      this.decisionTargetItem = item;
      this.deciding = null;
      this.decisionDialogIsOpen = true;
    },

    closeDecisionDialog() {
      this.decisionDialogIsOpen = false;
      this.decisionTargetItem = null;
      this.deciding = null;
    },

    async confirmDecision(type) {
      if (!this.decisionTargetItem) return;

      this.deciding = type;
      const payload = type === "approve" ? { approve: true } : { approve: false, reason: "" };

      try {
        const updated = await CompanyVouchersService.approve(
          this,
          this.decisionTargetItem.id,
          payload
        );

        const idx = this.items.findIndex((v) => v.id === this.decisionTargetItem.id);
        if (idx !== -1) this.$set(this.items, idx, { ...this.items[idx], ...updated });

        this.$store.commit("app/message", {
          msg: type === "approve" ? this.$t("Bon validé avec succès.") : this.$t("Bon rejeté."),
          color: "success",
        });

        await this.refresh();
      } catch (err) {
        const msg =
          err?.response?.status === 403
            ? type === "approve"
              ? this.$t("Vous n'avez pas les droits pour valider ce bon.")
              : this.$t("Vous n'avez pas les droits pour rejeter ce bon.")
            : this.$t("Une erreur s'est produite lors du traitement.");
        this.$store.commit("app/message", { msg, color: "error" });
      } finally {
        this.closeDecisionDialog();
      }
    },

    handleScroll() {
      // ✅ plus fiable que document.body.offsetHeight
      const el = document.documentElement;
      const bottom = el.scrollTop + window.innerHeight;
      if (bottom >= el.scrollHeight - 300) {
        this.loadMoreVouchers();
      }
    },
  },

  created() {
    if (this.$store.state.app.userPrivileges === null
        && typeof this.$store.dispatch === "function") {
      try { this.$store.dispatch("app/loadUserPrivileges") } catch (e) { /* ignore */ }
    }

    ShopDetailService.getprincipalAll().then((r) => {
      this.shops = [{ id: null, name: this.$t("Tout le réseau") }, ...(r || [])];
    });

    ProductDetailService.getprincipalAll().then((r) => {
      this.products = r?.results || r || [];
    });

    CompanyVehiclesService.get(this, null, { all: true }).then((r) => {
      this.vehicles = r?.results || r || [];
    });

    if (this.company && this.company.pk) {
      DepartmentService.getDepartment(this.company.pk).then((r) => {
        this.departments = r || [];
      });
    }

    if (this.department && this.department.id) {
      this.selectedDepartments = this.department.id;
    }
  },

  mounted() {
    this.getVoucherDebounced = debounce(async () => {
      await this.refresh();
    }, 500);

    (async () => {
      await this.refresh();
    })();

    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style scoped>
.voucher-detail-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

.stats-section {
  margin-top: 10px;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}

.stats-wrapper {
  padding: 0 5px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 0 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  border: none;
  position: relative;
  overflow: hidden;
}

.stat-card-main::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}

.stat-label-mobile {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
}

.stat-value-mobile {
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
}

.stat-unit-mobile {
  font-size: 15px;
  font-weight: 600;
}

.stat-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.stat-card-product {
  border-radius: 16px !important;
  padding: 0 20px;
  position: relative;
  overflow: hidden;
  transition: 0.3s;
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}

.stat-secondary-product {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.voucher-card {
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
}

.voucher-title {
  min-width: 0;
}

.voucher-title .veh {
  font-weight: 700;
  font-size: 16px;
  max-width: 60vw;
}

.voucher-title .driver {
  font-size: 12px;
  margin-top: 2px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px 16px;
}

.cell .label {
  font-size: 12px;
  color: #6b7280;
}

.cell .value {
  font-size: 14px;
  font-weight: 600;
}

.search-field,
.search-field-mobile {
  border-radius: 12px;
}

.blocked-row {
  background-color: #f8d7da !important;
}

@media (max-width: 360px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
